import { Howl, Howler } from 'howler';
import { settings } from '@/config';

interface SoundDef {
  src: string[];
  loop?: boolean;
  volume?: number;
  rate?: number;
  pool?: number;
}

/**
 * Howler.js audio manager (prepared only). Sets master volume, auto-mutes on
 * tab blur (proven rule), and provides a tiny load/play/stop API. Nothing is
 * loaded in Phase 01 — this is purely the integration seam.
 */
export class AudioManager {
  enabled = settings.audio.enabled;
  private readonly sounds = new Map<string, Howl>();
  /** Low-shelf EQ nodes spliced into a sound's Web Audio chain — see {@link setBass}. */
  private readonly bassFilters = new Map<string, BiquadFilterNode>();

  constructor() {
    Howler.volume(settings.audio.masterVolume);
    if (settings.audio.muteOnBlur) {
      window.addEventListener('blur', this.onBlur);
      window.addEventListener('focus', this.onFocus);
    }
  }

  load(name: string, def: SoundDef): Howl {
    const howl = new Howl({
      src: def.src,
      loop: def.loop ?? false,
      volume: def.volume ?? 1,
      rate: def.rate ?? 1,
      pool: def.pool ?? 1,
      preload: true,
    });
    this.sounds.set(name, howl);
    return howl;
  }

  get(name: string): Howl | undefined {
    return this.sounds.get(name);
  }

  /**
   * Live bass control for one (Web Audio) sound: splice a low-shelf BiquadFilter between the
   * sound's gain node and Howler's master bus, and drive its gain in dB (2026-07-19, user wants
   * the universal track's bass on a slider).
   *
   * Howler has no EQ API, so this reaches into the internals — `howl._sounds[0]._node` is the
   * per-sound GainNode, wired `_node → Howler.masterGain` at creation and never re-wired after
   * (play/stop only rebuild the BufferSource FEEDING `_node`), so a one-time re-route to
   * `_node → filter → masterGain` survives loops, pauses and rate changes. Everything is
   * defensive: if the internals ever change shape this silently degrades to "no EQ" rather than
   * breaking playback.
   */
  setBass(name: string, gainDb: number, freq = 200): void {
    const existing = this.bassFilters.get(name);
    if (existing) {
      existing.gain.value = gainDb;
      existing.frequency.value = freq;
      return;
    }
    if (!Howler.ctx || !Howler.masterGain) return; // Web Audio not unlocked yet
    const howl = this.sounds.get(name) as unknown as { _sounds?: Array<{ _node?: GainNode }> };
    const node = howl?._sounds?.[0]?._node;
    if (!node || typeof node.disconnect !== 'function') return;
    try {
      const filter = Howler.ctx.createBiquadFilter();
      filter.type = 'lowshelf';
      filter.frequency.value = freq;
      filter.gain.value = gainDb;
      node.disconnect();
      node.connect(filter);
      filter.connect(Howler.masterGain);
      this.bassFilters.set(name, filter);
    } catch {
      /* leave the stock chain in place — sound keeps playing, just without EQ */
    }
  }

  /** Play a sound. `volume` (0..1) and `rate` (playback speed / pitch, 1 = original) are set on
   *  the specific voice instance, so overlapping pooled plays can each have their own level and
   *  pitch. */
  play(name: string, volume?: number, rate?: number): number | undefined {
    const howl = this.sounds.get(name);
    if (!howl) return undefined;
    const id = howl.play();
    if (id !== undefined) {
      if (volume !== undefined) howl.volume(volume, id);
      if (rate !== undefined) howl.rate(rate, id);
    }
    return id;
  }

  /** Start a sound (once) and fade it up from silence to `volume` over `fadeMs`. Used for the
   *  looping ambient track. If it's already playing, this is a no-op so it can be called from
   *  several triggers (post-preloader + a first-gesture fallback) without restarting it. */
  playWithFade(name: string, volume: number, fadeMs: number): number | undefined {
    const howl = this.sounds.get(name);
    if (!howl || howl.playing()) return undefined;
    const id = howl.play();
    if (id !== undefined) {
      howl.volume(0, id);
      howl.fade(0, volume, fadeMs, id);
    }
    return id;
  }

  stop(name: string): void {
    this.sounds.get(name)?.stop();
  }

  setMasterVolume(volume: number): void {
    Howler.volume(volume);
  }

  mute(muted: boolean): void {
    Howler.mute(muted);
  }

  private onBlur = (): void => {
    Howler.mute(true);
  };

  private onFocus = (): void => {
    Howler.mute(false);
  };

  dispose(): void {
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);
    this.bassFilters.forEach((f) => f.disconnect());
    this.bassFilters.clear();
    this.sounds.forEach((howl) => howl.unload());
    this.sounds.clear();
  }
}
